import { Component, DestroyRef, HostListener, computed, effect, inject, signal, untracked } from '@angular/core'

import { ViewportService } from '../services/viewport.service'
import { WhatsNewService } from './whats-new.service'
import { WhatsNewDemoComponent, hasWhatsNewDemo } from './whats-new-demo.component'

/** The card's width on a laptop, and the gap between it and its button. */
const CARD_W = 360
const GAP = 12
const EDGE = 12

/** How long a step's button may be missing before the guide says what to do instead (pages load). */
const HINT_AFTER_MS = 2500

/** Where the card (or the "Show me" hint) sits on a laptop, next to its button. */
interface Place { top: number; left: number; arrowLeft: number; below: boolean }

/**
 * What's new card (CC 18/10/2026, mock-up approved the same day). Lives once in the app shell and
 * shows whatever WhatsNewService has active:
 * - phone: a sheet from the bottom over a dimmed page;
 * - laptop: a card beside the button it is about, the page still usable;
 * - "Show me": the real button pulses with a one-line hint until "Done" or they press it;
 * - from the bell, or on the first page after sign-in: the card on its own, with "Take me there".
 */
@Component({
  selector: 'app-whats-new-card',
  standalone: true,
  imports: [WhatsNewDemoComponent],
  template: `
    @if (active(); as a) {
      @if (a.mode === 'card') {
        @if (sheet() || center()) {
          <div class="wn-backdrop" (click)="later()" aria-hidden="true"></div>
        }
        <section class="wn-card" [class.wn-card--sheet]="sheet()" [class.wn-card--center]="center()" [class.wn-card--below]="place()?.below"
          role="dialog" aria-modal="false" aria-labelledby="wn-title" data-testid="whats-new-card"
          [style.top.px]="place()?.top" [style.left.px]="place()?.left">
          @if (!sheet() && place(); as p) {
            <span class="wn-arrow" [style.left.px]="p.arrowLeft" aria-hidden="true"></span>
          }
          @if (sheet()) { <span class="wn-grab" aria-hidden="true"></span> }
          <div class="wn-head">
            <span class="wn-eyebrow">{{ a.landing ? 'NEW FOR YOU' : a.fromBell ? "WHAT'S NEW" : 'NEW ON THIS PAGE' }}</span>
            <button type="button" class="wn-x" (click)="later()" aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          @if (a.item.video_url) {
            <video class="wn-video" [src]="a.item.video_url" [poster]="a.item.poster_url || ''" [muted]="true" loop playsinline
              [autoplay]="!reducedMotion" [controls]="reducedMotion" preload="metadata"
              [attr.aria-label]="'Short demo: ' + a.item.title"></video>
          } @else if (hasDemo(a.item.slug)) {
            <app-whats-new-demo class="wn-video" [slug]="a.item.slug" />
          } @else if (a.item.poster_url) {
            <img class="wn-video" [src]="a.item.poster_url" alt="" />
          }
          <h2 class="wn-title" id="wn-title">{{ a.item.title }}</h2>
          <p class="wn-body">{{ a.item.body }}</p>
          <div class="wn-actions">
            @if (a.anchor) {
              <button type="button" class="wn-btn" data-testid="whats-new-got-it" (click)="whatsNew.gotIt()">Got it</button>
              <button type="button" class="wn-btn wn-btn--primary" data-testid="whats-new-show-me" (click)="whatsNew.showMe()">Show me</button>
            } @else {
              <button type="button" class="wn-btn" data-testid="whats-new-got-it" (click)="whatsNew.gotIt()">{{ a.landing ? 'Got it' : 'Close' }}</button>
              @if (a.item.link) {
                <button type="button" class="wn-btn wn-btn--primary" data-testid="whats-new-go" (click)="whatsNew.goThere()">Take me there</button>
              }
            }
          </div>
        </section>
      } @else {
        <div class="wn-hint" [class.wn-hint--sheet]="sheet()" [class.wn-card--below]="place()?.below" role="status"
          data-testid="whats-new-hint" [style.top.px]="place()?.top" [style.left.px]="place()?.left">
          @if (!sheet() && place(); as p) {
            <span class="wn-arrow wn-arrow--light" [style.left.px]="p.arrowLeft" aria-hidden="true"></span>
          }
          <span class="wn-hint__text">Here it is: <strong>{{ a.item.title }}</strong>. Give it a try.</span>
          <button type="button" class="wn-hint__done" (click)="whatsNew.close()">Done</button>
        </div>
      }
    } @else if (guide(); as g) {
      <!-- The guide after "Take me there": one step at a time; the step's button pulses. -->
      <div class="wn-guide" [class.wn-guide--sheet]="sheet()" role="status" aria-live="polite" data-testid="whats-new-guide">
        <div class="wn-guide__head">
          <span class="wn-eyebrow">{{ g.item.title }} · Step {{ g.step + 1 }} of {{ g.item.steps?.length }}</span>
          <button type="button" class="wn-x" (click)="whatsNew.closeGuide()" aria-label="Close the guide">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <p class="wn-guide__text" data-testid="whats-new-guide-text">{{ guideText() }}</p>
        @if (seeStepReady()) {
          <button type="button" class="wn-btn wn-btn--primary wn-guide__done" data-testid="whats-new-guide-done" (click)="whatsNew.finishGuide()">Got it</button>
        }
        <span class="wn-guide__dots" aria-hidden="true">
          @for (s of g.item.steps; track $index) {
            <span class="wn-guide__dot" [class.wn-guide__dot--on]="$index <= g.step"></span>
          }
        </span>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }
    .wn-backdrop { position: fixed; inset: 0; z-index: 1290; background: rgba(4, 7, 14, 0.62); }
    .wn-card {
      position: fixed; z-index: 1300; width: ${CARD_W}px; box-sizing: border-box; padding: 16px;
      background: var(--scios-surface-2); color: var(--scios-text-primary);
      border: 1px solid var(--scios-border); border-radius: 14px; box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55);
      font-family: var(--font-family-primary); animation: wn-pop .22s ease-out;
    }
    .wn-card--sheet {
      left: 0; right: 0; bottom: 0; top: auto; width: auto; max-height: 92vh; overflow-y: auto;
      border-radius: 20px 20px 0 0; border-bottom: 0; padding: 10px 16px calc(20px + env(safe-area-inset-bottom));
      animation: wn-up .28s ease-out;
    }
    .wn-card--center {
      left: 50%; top: 50%; width: 440px; max-width: calc(100vw - 32px); max-height: 90vh; overflow-y: auto;
      transform: translate(-50%, -50%); animation: wn-fade .2s ease-out;
    }
    .wn-grab { display: block; width: 40px; height: 4px; border-radius: 2px; background: var(--scios-border-hover); margin: 0 auto 12px; }
    .wn-arrow {
      position: absolute; bottom: -8px; width: 14px; height: 14px; background: var(--scios-surface-2);
      border-right: 1px solid var(--scios-border); border-bottom: 1px solid var(--scios-border); transform: rotate(45deg);
    }
    .wn-card--below .wn-arrow { bottom: auto; top: -8px; transform: rotate(225deg); }
    .wn-arrow--light { background: var(--scios-text-primary); border: 0; }
    .wn-head { display: flex; align-items: center; justify-content: space-between; }
    .wn-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .1em; color: var(--accent-primary); }
    .wn-x {
      width: 36px; height: 36px; margin: -8px -8px 0 0; border: 0; border-radius: 8px; background: none;
      color: var(--scios-text-secondary); display: grid; place-items: center; cursor: pointer;
    }
    .wn-video { display: block; width: 100%; aspect-ratio: 16 / 9; margin-top: 8px; border-radius: 10px; background: #0E2A24; object-fit: cover; }
    .wn-title { margin: 14px 0 0; font-size: 18px; font-weight: 600; }
    .wn-body { margin: 6px 0 0; font-size: 14px; line-height: 1.45; color: var(--scios-text-secondary); }
    .wn-actions { margin-top: 16px; display: flex; justify-content: flex-end; gap: 8px; }
    .wn-btn {
      min-height: 40px; padding: 0 16px; border: 1px solid var(--scios-border); border-radius: 10px; background: none;
      color: var(--scios-text-primary); font: 500 14px var(--font-family-primary); cursor: pointer;
    }
    .wn-btn--primary { border-color: var(--accent-primary); background: var(--accent-primary); color: var(--scios-base, #080d1a); font-weight: 600; }
    .wn-btn:focus-visible, .wn-x:focus-visible, .wn-hint__done:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }
    .wn-card--sheet .wn-title { font-size: 20px; }
    .wn-card--sheet .wn-body { font-size: 15px; }
    .wn-card--sheet .wn-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 18px; }
    .wn-card--sheet .wn-actions .wn-btn:only-child { grid-column: 1 / -1; }
    .wn-card--sheet .wn-btn { min-height: 48px; font-size: 15px; }

    .wn-hint {
      position: fixed; z-index: 1300; width: 320px; box-sizing: border-box; padding: 12px 12px 12px 14px;
      display: flex; align-items: center; gap: 12px; border-radius: 12px;
      background: var(--scios-text-primary); color: var(--scios-base, #080d1a);
      font: 14px/1.4 var(--font-family-primary); box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4); animation: wn-pop .22s ease-out;
    }
    .wn-hint--sheet { left: 12px; right: 12px; top: auto; bottom: calc(16px + env(safe-area-inset-bottom)); width: auto; }
    .wn-hint__text { flex: 1; }
    .wn-hint__done {
      flex: none; min-height: 44px; padding: 0 16px; border: 0; border-radius: 10px;
      background: var(--scios-base, #080d1a); color: var(--scios-text-primary); font: 600 14px var(--font-family-primary); cursor: pointer;
    }
    .wn-guide {
      position: fixed; z-index: 1300; top: 76px; left: 50%; width: 420px; max-width: calc(100vw - 24px); box-sizing: border-box;
      transform: translateX(-50%); padding: 12px 14px 12px; border-radius: 12px;
      background: var(--scios-surface-2); color: var(--scios-text-primary); border: 1px solid var(--accent-primary);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45); font-family: var(--font-family-primary); animation: wn-fade .2s ease-out;
    }
    .wn-guide--sheet { left: 12px; width: auto; transform: none; }
    .wn-guide__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .wn-guide__text { margin: 4px 0 0; font-size: 15px; line-height: 1.4; }
    .wn-guide__done { margin-top: 10px; min-height: 44px; }
    .wn-guide__dots { display: flex; gap: 6px; margin-top: 10px; }
    .wn-guide__dot { width: 18px; height: 4px; border-radius: 2px; background: var(--scios-border); }
    .wn-guide__dot--on { background: var(--accent-primary); }
    @keyframes wn-pop { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
    @keyframes wn-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes wn-up { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { .wn-card, .wn-hint, .wn-guide { animation: none; } }
  `],
})
export class WhatsNewCardComponent {
  protected whatsNew = inject(WhatsNewService)
  private viewport = inject(ViewportService)

  readonly active = this.whatsNew.active
  readonly guide = this.whatsNew.guide
  protected readonly hasDemo = hasWhatsNewDemo
  /** The current step's button is missing for a while: say the step's hint instead. */
  private readonly stepMissing = signal(false)

  /** The current step's button, when it is on screen. */
  private readonly guideSpot = computed(() => {
    this.whatsNew.spotsVersion()
    this.tick()
    const g = this.guide()
    if (!g || this.active()) return null
    return this.whatsNew.spotFor(g.item.steps?.[g.step]?.spot)
  })

  /** A step to look at whose card is on screen: the bar offers "Got it". */
  readonly seeStepReady = computed(() => {
    const g = this.guide()
    return !!g?.item.steps?.[g.step]?.see && !!this.guideSpot()
  })

  readonly guideText = computed(() => {
    const g = this.guide()
    const step = g?.item.steps?.[g.step]
    if (!step) return ''
    return !this.guideSpot() && this.stepMissing() && step.hint ? step.hint : step.text
  })
  protected readonly reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  /** Bumped on scroll and resize so the card follows its button. */
  private readonly tick = signal(0)

  /** Phone: the sheet from the bottom for every card and hint, and the guide too -- unless the
   *  step's button is low on the screen, then the guide sits at the top (Release 5 tidy, 03/11/2026). */
  readonly sheet = computed(() => this.viewport.isPhone())
  /** Laptop, with no button to point at (sign-in, the bell): a card in the middle of the screen. */
  readonly center = computed(() => !this.viewport.isPhone() && this.active()?.mode === 'card' && !this.active()?.anchor)

  readonly place = computed<Place | null>(() => {
    this.tick()
    const a = this.active()
    if (!a?.anchor || this.viewport.isPhone()) return null
    const r = a.anchor.getBoundingClientRect()
    const w = a.mode === 'card' ? CARD_W : 320
    // The card goes above its button when there is room for it (video + text ≈ 420 px), else
    // below. The "Show me" hint goes below when it fits, so it never covers the top bar.
    const below = a.mode === 'card' ? r.top < 420 + GAP : r.bottom + GAP + 90 < window.innerHeight
    const left = Math.min(Math.max(EDGE, r.left + r.width / 2 - w / 2), window.innerWidth - w - EDGE)
    const arrowLeft = Math.min(Math.max(16, r.left + r.width / 2 - left - 7), w - 30)
    // Above: the card's bottom edge sits GAP over the button; its height is not known up front, so
    // anchor by bottom through top = r.top - GAP - height, measured after render (see effect).
    const top = below ? r.bottom + GAP : r.top - GAP
    return { top, left, arrowLeft, below }
  })

  constructor () {
    // "Above": shift the card up by its own height once it has rendered.
    effect(() => {
      const p = this.place()
      if (!p || p.below) return
      queueMicrotask(() => {
        const el = document.querySelector<HTMLElement>('.wn-card:not(.wn-card--sheet), .wn-hint:not(.wn-hint--sheet)')
        if (el) el.style.top = `${Math.max(EDGE, p.top - el.offsetHeight)}px`
      })
    })
    // Phone: pin the sheet to the part of the page actually on screen. A page wider than the phone
    // (a table that sticks out) widens what "fixed" means, which pushed the buttons off-screen.
    effect(() => {
      this.tick()
      this.guide()
      if (!this.sheet() || (!this.active() && !this.guide())) return
      queueMicrotask(() => {
        const vv = window.visualViewport
        const guideEl = document.querySelector<HTMLElement>('.wn-guide--sheet')
        if (guideEl && vv) {
          guideEl.style.left = `${vv.offsetLeft + 12}px`
          guideEl.style.width = `${vv.width - 24}px`
          // At the top it covered the page's own header and tab row, which is where most steps
          // point; at the bottom it covers nothing the step needs.
          const spot = this.guideSpot()?.getBoundingClientRect()
          const spotLow = !!spot && spot.top > vv.height * 0.55
          guideEl.style.top = spotLow
            ? `${vv.offsetTop + 64}px`
            : `${vv.offsetTop + vv.height - guideEl.offsetHeight - 16}px`
        }
        const el = document.querySelector<HTMLElement>('.wn-card--sheet, .wn-hint--sheet')
        if (!el || !vv) return
        const hint = el.classList.contains('wn-hint--sheet')
        const inset = hint ? 12 : 0
        el.style.right = 'auto'
        el.style.bottom = 'auto'
        el.style.left = `${vv.offsetLeft + inset}px`
        el.style.width = `${vv.width - inset * 2}px`
        el.style.maxHeight = `${Math.round(vv.height * 0.92)}px`
        el.style.top = `${vv.offsetTop + vv.height - el.offsetHeight - (hint ? 16 : 0)}px`
      })
    })
    // "Show me": the real button pulses; pressing it ends the hint (they are using it).
    let pulsing: HTMLElement | null = null
    const used = () => this.whatsNew.close()
    effect(() => {
      const a = this.active()
      const el = a?.mode === 'spot' ? a.anchor : null
      if (pulsing && pulsing !== el) {
        pulsing.classList.remove('wn-spot')
        pulsing.removeEventListener('click', used)
      }
      if (el && pulsing !== el) {
        el.classList.add('wn-spot')
        el.addEventListener('click', used, { once: true })
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
      pulsing = el
    })
    // A later step's button already on screen: jump ahead (one team, or they found the way).
    effect(() => {
      this.whatsNew.spotsVersion()
      this.tick()
      if (this.guide()) untracked(() => this.whatsNew.syncGuide())
    })
    // The guide: the current step's button pulses until it is pressed or the guide ends.
    let guidePulsing: HTMLElement | null = null
    effect(() => {
      const el = this.guideSpot()
      if (guidePulsing && guidePulsing !== el) guidePulsing.classList.remove('wn-spot')
      if (el && guidePulsing !== el) {
        el.classList.add('wn-spot')
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
      guidePulsing = el
    })
    // A step whose button is not there (no playlists yet): after a moment, its hint instead.
    let missingTimer: ReturnType<typeof setTimeout> | null = null
    effect(() => {
      const g = this.guide()
      const present = !!this.guideSpot()
      if (missingTimer) clearTimeout(missingTimer)
      this.stepMissing.set(false)
      if (g && !present) missingTimer = setTimeout(() => this.stepMissing.set(true), HINT_AFTER_MS)
    })
    // Any scroll, including inside a dialog or a side panel, moves the button.
    const onScroll = () => this.onMove()
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    window.visualViewport?.addEventListener('resize', onScroll)
    window.visualViewport?.addEventListener('scroll', onScroll)
    inject(DestroyRef).onDestroy(() => {
      pulsing?.classList.remove('wn-spot')
      guidePulsing?.classList.remove('wn-spot')
      if (missingTimer) clearTimeout(missingTimer)
      document.removeEventListener('scroll', onScroll, { capture: true })
      window.visualViewport?.removeEventListener('resize', onScroll)
      window.visualViewport?.removeEventListener('scroll', onScroll)
    })
  }

  /** Close without answering: the card may come back next visit (at most three times). */
  later (): void {
    this.whatsNew.close()
  }

  @HostListener('window:resize')
  onMove (): void {
    if (this.active() || this.guide()) this.tick.update((n) => n + 1)
  }

  @HostListener('document:keydown.escape')
  onEscape (): void {
    if (this.active()) this.later()
  }
}
